import * as React from 'react';
import {
    Button, Checkbox, FormControl,
    Grid, IconButton, InputLabel, MenuItem, Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Switch, Alert, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import ToolbarSelect from "./ToolbarSelect";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {useEffect, useState} from "react";
import DataService from "../DataService";
import TextTable from "./TextTable";
import VisuaSearchTagModal from "./VisuaSearchTagModal";

const SHORT_TEXT_MIN_LENGTH = 3;

/**
 * Counts user-perceived characters (grapheme clusters), not UTF-16 code units.
 * e.g. "😀" and "é" (e + combining accent) each count as 1, while String.length gives 2.
 * @param {string} str
 * @returns {number}
 */
const countCharacters = (str) => {
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
        let count = 0;
        // eslint-disable-next-line no-unused-vars
        for (const _ of new Intl.Segmenter(undefined, {granularity: 'grapheme'}).segment(str)) count++;
        return count;
    }
    // Fallback: count Unicode code points
    return Array.from(str).length;
};

/**
 * @typedef {Object} TextItem
 * @property {number} id
 * @property {number} brandId
 * @property {string} text
 * @property {number} threshold
 * @property {number} status
 * @property {Record<string, any>=} meta
 * @property {Record<string, any>=} verification
 */

/**
 * @typedef {Object} Brand
 * @property {number} id
 * @property {TextItem[]=} items
 */
/**
 * @param {{ brand?: Brand, setItems: (items: TextItem[]) => void, showingContext?: boolean }} props
 */
function BrandTextManager({ brand = /** @type {Brand} */ ({}), setItems, showingContext = false }) {
    const TextListInit = brand?.items?? []
    const [addTextValue, setAddTextValue] = useState('');
    const [threshold, setThreshold] = useState(1)
    const [textList, setListOfText] = useState(TextListInit);
    const [selectedItem, setSelectedItem] = useState(/** @type {TextItem|null} */ (null));
    const [openTagModal, setOpenTagModal] = useState(false);
    const [openShortTextWarning, setOpenShortTextWarning] = useState(false);
    const [errorMessage, setErrorMessage] = useState(/** @type {string|null} */ (null));

    const addTextLength = countCharacters(addTextValue.trim());
    const normalizeMeta = (meta) => {
        if (!meta) return {};

        // Sometimes meta arrives stringified
        if (typeof meta === 'string') {
            try {
                const parsed = JSON.parse(meta);
                return normalizeMeta(parsed);
            } catch (e) {
                return {};
            }
        }

        if (typeof meta !== 'object') return {};

        // Sometimes meta is nested like { meta: {...} }
        if (
            Object.keys(meta).length === 1
            && Object.prototype.hasOwnProperty.call(meta, 'meta')
            && meta.meta
            && typeof meta.meta === 'object'
        ) {
            return meta.meta;
        }

        return meta;
    };

    const normalizeTextItem = (textItem) => {
        if (!textItem || typeof textItem !== 'object') return textItem;
        return {
            ...textItem,
            meta: normalizeMeta(textItem.meta),
        };
    };

    const handleChange = (event) => {
        setThreshold(event.target.value);
    };

    /**
     * Runs a text request and shows any failure as an inline alert instead of a toast.
     * @param {Promise<any>} request
     * @param {(result: any) => void} onSuccess
     */
    const runTextRequest = (request, onSuccess) => {
        setErrorMessage(null);
        request
            .then(onSuccess)
            .catch(err => setErrorMessage(err?.message || 'Something went wrong. Please try again.'));
    }

    const addNewTextToBrand = () => {
        runTextRequest(
            DataService.addTextToBrand(addTextValue, threshold, brand.id, {silent: true}),
            res => {
                if (res) {
                    localDataUpdate(res);
                    setThreshold(0.8);
                    setAddTextValue('');
                }
            }
        );
    }

    const handleAddTextClick = () => {
        if (addTextLength < SHORT_TEXT_MIN_LENGTH) {
            setOpenShortTextWarning(true);
            return;
        }
        addNewTextToBrand();
    }

    const localDataUpdate = (result) => {
        const items = result?.items ?? [];
        const textToRender = items
            .filter(el => el.brandId === brand.id)
            .map(normalizeTextItem);
        setListOfText(textToRender);
        setItems(textToRender);

    }

    const saveNewMeta = (meta) => {
        setOpenTagModal(false);
        if (!selectedItem?.id) {
            return;
        }

        runTextRequest(
            DataService.putTextItemMeta(selectedItem.id, meta, {silent: true}),
            (response) => {
                const status = response?.status || 0;
                if (status !== 200) {
                    return;
                }

                const updatedItems = textList.map((textItem) => (
                    textItem.id === selectedItem.id
                        ? {
                            ...textItem,
                            meta,
                        }
                        : textItem
                ));

                setListOfText(updatedItems);
                setItems(updatedItems);
            }
        );
    };
    
    useEffect(() => {
        const normalized = (brand?.items ?? []).map(normalizeTextItem);
        setListOfText(normalized);
    }, [brand])
    useEffect(() => {
        const calcConfidence = () => {
            const tlen = addTextValue.length;
            if (tlen < 10) return 1;
            if (tlen <= 19) return 0.9;
            if (tlen <= 29) return 0.8;
            return 0.7
        }

        setThreshold(
            calcConfidence()
        )
    }, [addTextValue])

    return (
        <Grid container={true}>
            <Grid container={true}>
                <Grid item xs={12}>
                    <Alert severity="warning">
                        We strongly recommend that you do not change the suggested threshold
                    </Alert>
                </Grid>
                {
                    errorMessage &&
                    <Grid item xs={12} sx={{mt: 2}}>
                        <Alert severity="error" onClose={() => setErrorMessage(null)}>
                            {errorMessage}
                        </Alert>
                    </Grid>
                }
            </Grid>
            <Grid
                container={true}
                alignItems={'center'}
                spacing={2}
                sx={{mb: 2}}
            >
                <Grid item xs={7}>
                    <TextField
                        id="textValue"
                        label="Add Text String"
                        variant={'standard'}
                        fullWidth
                        value={addTextValue}
                        onChange={
                            e => {
                                setAddTextValue(e.target.value)
                            }
                        }
                    />
                </Grid>
                <Grid item xs={2}>
                    <FormControl variant="standard" sx={{ m: 1, minWidth: 120, width: '100%' }}>
                        <InputLabel >Threshold</InputLabel>
                        <Select
                            labelId="demo-simple-select-standard-label"
                            id="demo-simple-select-standard"
                            value={threshold}
                            onChange={handleChange}
                            label="threshold"
                            fullWidth={true}
                        >
                            {
                                [
                                    0.2,
                                    0.4,
                                    0.5,
                                    0.6,
                                    0.7,
                                    0.8,
                                    0.9,
                                    1,
                                ].map(option => (
                                    <MenuItem
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3} textAlign={'right'} marginLeft={'auto'}>
                    <Button
                        fullWidth={true}
                        variant={'contained'}
                        onClick={handleAddTextClick}
                        disabled={addTextLength === 0}
                    >
                        Add Text
                    </Button>
                </Grid>
            </Grid>

            <Grid container={true}>
                <Grid item xs={12}
                      sx={{
                          maxHeight: '500px',
                          overflow: 'auto',
                      }}
                >
                    {
                        textList.length > 0 ?
                        <Table>
                            <TableHead
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    background: '#fff',
                                    zIndex: 2,
                                }}
                            >
                                <TableRow>
                                    <TableCell>
                                        Text
                                    </TableCell>
                                    {showingContext && (
                                        <TableCell align="center" style={{ width: '88px' }}>
                                            Context
                                        </TableCell>
                                    )}
                                    <TableCell
                                        style={{width: "20%"}}
                                    >
                                        Threshold
                                    </TableCell>
                                    <TableCell
                                        align={'center'}
                                        style={{width: "10%"}}
                                    >
                                        Toggle
                                    </TableCell>
                                    <TableCell
                                        align={'center'}
                                        style={{width: "10%"}}
                                    >
                                        Delete
                                    </TableCell>
                                    <TableCell
                                        align={'center'}
                                        style={{width: "10%"}}
                                    >
                                        Meta
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    textList.sort((a, b) => (a.text > b.text) ? 1 : -1).map( (item, index) => (
                                        <TableRow
                                            key={index}
                                        >
                                            <TableCell>{item.text}</TableCell>
                                            {showingContext && (
                                                <TableCell align="center" style={{ width: '88px' }}>
                                                    <Checkbox
                                                        checked={Boolean(item?.verification)}
                                                        disabled
                                                        size="small"
                                                        inputProps={{
                                                            'aria-label': item?.verification
                                                                ? 'Context verification present'
                                                                : 'No context verification',
                                                        }}
                                                        sx={{
                                                            p: 0.5,
                                                            pointerEvents: 'none',
                                                            '&.Mui-disabled': { color: 'inherit' },
                                                        }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell
                                                style={{width: "10%"}}
                                            >
                                                <Select
                                                    labelId="demo-simple-select-standard-label"
                                                    id="demo-simple-select-standard"
                                                    value={item.threshold}
                                                    onChange={(event) => {
                                                        const {
                                                            value
                                                        } = event?.target;
                                                        runTextRequest(
                                                            DataService.putText(item.id, value, {silent: true}),
                                                            localDataUpdate
                                                        );
                                                    }}
                                                    label="threshold"
                                                    fullWidth={true}
                                                    variant={'standard'}
                                                >
                                                    {
                                                        [
                                                            0.2,
                                                            0.4,
                                                            0.5,
                                                            0.6,
                                                            0.7,
                                                            0.8,
                                                            0.9,
                                                            1,
                                                        ].map(option => (
                                                            <MenuItem
                                                                key={option}
                                                                value={option}
                                                                sx={{
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                {option}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </Select>
                                            </TableCell>
                                            <TableCell
                                                align={'center'}
                                                style={{width: "10%"}}
                                            >
                                                <Switch
                                                    checked={item.status === 1}
                                                    onChange={e => {
                                                        const value = e.target.checked;
                                                        const {id, threshold} = item;
                                                        runTextRequest(
                                                            value
                                                                ? DataService.putText(id, threshold, {silent: true})
                                                                : DataService.deleteText(id, false, {silent: true}),
                                                            localDataUpdate
                                                        );
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                style={{width: "10%"}}
                                            >
                                                <IconButton
                                                    color={'error'}
                                                    onClick={() => {
                                                        runTextRequest(
                                                            DataService.deleteText(item.id, true, {silent: true}),
                                                            localDataUpdate
                                                        );
                                                    }}
                                                >
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </TableCell>
                                            <TableCell
                                                align={'center'}
                                                style={{width: "10%"}}
                                            >
                                                <Tooltip title={item?.meta && Object.keys(item.meta).length > 0 ? 'Edit meta tags' : 'Add meta tags'}>
                                                    <IconButton
                                                        color={item?.meta && Object.keys(item.meta).length > 0 ? 'primary' : 'default'}
                                                        onClick={() => {
                                                            setSelectedItem(normalizeTextItem(item));
                                                            setOpenTagModal(true);
                                                        }}
                                                    >
                                                        <LocalOfferIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                            :
                            <Typography variant={'h6'}>
                                No text for this brand yet
                            </Typography>
                    }

                </Grid>
            </Grid>
            <VisuaSearchTagModal
                open={openTagModal}
                handleClose={_ => setOpenTagModal(false)}
                handleSave={saveNewMeta}
                item={selectedItem}
            />
            <Dialog
                open={openShortTextWarning}
                onClose={() => setOpenShortTextWarning(false)}
                aria-labelledby="short-text-warning-title"
            >
                <DialogTitle id="short-text-warning-title">Warning</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        You are trying to add a very short text string. We strongly recommend that you only add
                        text strings that are at least three characters long (and ideally longer than that).
                    </DialogContentText>
                    <DialogContentText sx={{mb: 2}}>
                        Enabling this text string may lead to a higher-than-desired rate of false positive detections.
                    </DialogContentText>
                    <DialogContentText>
                        Are you sure you want to add this text string?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant={'contained'}
                        onClick={() => {
                            setOpenShortTextWarning(false);
                            addNewTextToBrand();
                        }}
                    >
                        Yes
                    </Button>
                    <Button
                        onClick={() => setOpenShortTextWarning(false)}
                        autoFocus
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}


export default BrandTextManager;
